import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Modal,
  Select,
  TextField,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { RxCross1 } from "react-icons/rx";
import { useEffect, useState } from "react";
import { HttpService } from "../../service/HttpService";
import { TextareaAutosize } from "@mui/base/TextareaAutosize";
import FileInput from "../FileInput/FileInput";
import { useAuth } from "../../context/AuthProvider";
import { splitDuration } from "../../Mixin/DisplayDuration";
import SelectInput from "../SelectInput/SelectInput";

const AddEditActivity = ({
  open,
  handleClose,
  activity_id,
  isAdd,
  companyId,
  locationId,
  fetchActivity,
}) => {
  const [milestoneNames, setMilestoneNames] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [apiError, setApiError] = useState("");
  const { setShowLoginPopup, showLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);

  const autoSequenceOrder = async (payload) => {
    return await httpService
      .post("activity/check-auto-sequence", payload)
      .then((response) => {
        if (response?.data?.max_sort_order) {
          formik.setFieldValue("sort_order", response?.data?.max_sort_order);
        } else {
          formik.setFieldValue("sort_order", "");
        }
        return response?.data?.max_sort_order;
      })
      .catch((error) => {
        return error;
      });
  };

  useEffect(() => {
    fetchCompanies();
    if (companyId > 0) {
      getLocationByCompany(companyId);
    }
    if (locationId > 0) {
      getMilestoneByLocation(locationId);
    }
  }, [companyId, locationId, showLoginPopup]);

  //fetch data for edit time
  useEffect(() => {
    // console.log("useeffect running");
    if (activity_id !== "") {
      httpService
        .get(`activity/get_activity/${activity_id}`)
        .then((response) => {
          const { days, hours, minutes } =
            splitDuration(
              response?.data[0].days,
              response?.data[0].hours,
              response?.data[0].minutes
            ) || {};

          formik.setValues({
            name: response?.data[0].name,
            durationDays: days || 0,
            durationHours: hours || 0,
            durationMinute: minutes || 0,
            company: response?.data[0].company_id,
            location: response?.data[0].location_id,
            milestone: response?.data[0].milestone_id || "",
            description: response?.data[0].description,
            attached: response?.data[0].attached || [],
            sort_order: response?.data[0].sort_order || "",
            min: response?.data[0].min_employee || "",
            max: response?.data[0].max_employee || "",
            version: response?.data[0].version || "",
            root_image: response?.data[0].root_image_status,
            top_image: response?.data[0].top_image_status,
            side_image: response?.data[0].side_image_status,
          });
          if (response) {
            if (response?.data[0].company_id) {
              getLocationByCompany(response?.data[0].company_id);
            }
            if (response?.data[0].location_id) {
              getMilestoneByLocation(response?.data[0].location_id);
            }
          }
        });
    } else {
      formik.resetForm();
    }
  }, [activity_id]);

  const fetchCompanies = () => {
    httpService
      .get("company/get_companies")
      .then((response) => {
        setCompanies(response?.data?.data);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
      });
  };

  const handleChangeLocationMilestone = (key, id) => {
    if (key === "company") {
      // Call the API to fetch location data based on the selected company_id
      getLocationByCompany(id);
      formik.setFieldValue("location", "");
      formik.setFieldValue("milestone", "");
      setLocations([]);
      setMilestoneNames([]);
    }

    if (key === "location") {
      // Call the API to fetch milestone data based on the selected location_id
      getMilestoneByLocation(id);
      formik.setFieldValue("milestone", "");
      setMilestoneNames([]);
    }
  };

  const getLocationByCompany = (id) => {
    return httpService
      .get(`location/get_location/company/${id}`)
      .then((response) => {
        setLocations(response?.data);
        if (response?.data.length === 0) {
          formik.setFieldValue("location", "");
        }
      })
      .catch((error) => {
        console.error("Error fetching location data:", error);
      });
  };

  const getMilestoneByLocation = async (id) => {
    try {
      const response = await httpService.get(
        `milestone/location/get_milestones/${id}`
      );
      setMilestoneNames(response?.data);
      if (response?.data.length === 0) {
        formik.setFieldValue("milestone", "");
      }
    } catch (error) {
      console.error("Error fetching milestone data:", error);
    }
  };

  const handleCloseSave = () => {
    formik.resetForm();
    handleClose();
    setLocations([]);
    setMilestoneNames([]);
  };

  const matchSequenceOrder = async (payload) => {
    return await httpService
      .post("activity/check-sequence", payload)
      .then((response) => {
        setApiError("");
        return response;
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setApiError(error?.message);
        return error;
      });
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      durationDays: "",
      durationHours: "",
      durationMinute: "",
      milestone: "",
      company: (companyId > 0 && companyId) || "",
      location: (locationId > 0 && locationId) || "",
      description: "",
      file: [],
      attached: [],
      sort_order: "",
      min: "",
      max: "",
      version: "",
      root_image: 1,
      top_image: 1,
      side_image: 1,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required("Please Enter Activity Name")
        .max(200, "Activity Name must be less than 200 characters"),
      company: Yup.string().required("Please Select Company"),
      location: Yup.string().required("Please Select Location"),
      milestone: Yup.string()
        // .min(1, "Please Select at least one Milestone")
        .required("Please Select Milestone"),
      durationDays: Yup.number().nullable(),
      durationHours: Yup.number().nullable().max(24, "Maximum 24 hours"),
      durationMinute: Yup.number().nullable().max(60, "Maximum 60 minutes"),
      // root_image: Yup.number().nullable(),
      // top_image: Yup.number().nullable(),
      // side_image: Yup.number().nullable(),
      description: Yup.string().max(
        2000,
        "Description must be less than 2000 characters"
      ),
      min: Yup.number()
        .typeError("Min. Employee must be a number") // Error message for non-numeric input
        .integer("Min. Employee must be an integer") // Ensure min is an integer
        .min(0, "Min. Employee must be greater than or equal to 0"), // Ensure min is greater than or equal to 0
      max: Yup.number()
        .typeError("Max. Employee must be a number") // Error message for non-numeric input
        .integer("Max. Employee must be an integer") // Ensure max is an integer
        .min(
          Yup.ref("min"),
          "Max. Employee must be greater than or equal to Min. Employee"
        ), // Ensure max is greater than or equal to min

      file: Yup.mixed()
        // .required("Attachment Is Required")
        .test("fileSize", "Attachment size must be less than 10MB", (value) => {
          if (!value || typeof value !== "object") return true; // Skip if no files provided or value is not an object
          const files = Object.values(value); // Convert object values to an array of files
          return files.every((file) => file.size <= 10485760); // Check size of each file
        })
        .test("fileFormat", "Invalid file format", (value) => {
          if (!value || typeof value !== "object") return true; // Skip if no files provided or value is not an object
          const files = Object.values(value); // Convert object values to an array of files
          if (files.length === 0) return true; // Skip if no files provided
          const allowedExtensions = {
            video: ["mp4", "webm", "mov"],
            pdf: ["pdf"],
            document: ["doc", "docx"],
            spreadsheet: ["xls", "xlsx", "csv"],
            image: ["jpg", "png", "jpeg", "gif", "webp"],
          };
          return files.every((file) => {
            if (!file || !file.name) return false; // If file or file name is undefined, return false
            const fileExtension = file?.name?.split(".").pop().toLowerCase();
            const fileType = Object.keys(allowedExtensions).find((type) =>
              allowedExtensions[type].includes(fileExtension)
            );
            return !!fileType;
          });
        }),
    }).test("at-least-one", null, (values) => {
      const { durationDays, durationHours, durationMinute } = values;
      if (!durationDays && !durationHours && !durationMinute) {
        return new Yup.ValidationError(
          "Please Enter One Duration",
          null,
          "durationDays"
        );
      }
      if (durationDays < 0 || durationHours < 0 || durationMinute < 0) {
        return new Yup.ValidationError(
          "Please Enter Valid Duration",
          null,
          "durationDays"
        );
      }
      return true;
    }),
    // .test(
    //   "at-least-one-image",
    //   "At least one image type must be selected",
    //   (values) => {
    //     const { root_image, top_image, side_image } = values;
    //     if (root_image === 0 && top_image === 0 && side_image === 0) {
    //       return new Yup.ValidationError(
    //         "Select at least one Image Category",
    //         null,
    //         "root_image"
    //       ); // Return false if none of the image type checkboxes are selected
    //     }
    //     return true; // Return true if at least one image type checkbox is selected
    //   }
    // ),
    onSubmit: async (values) => {
      // console.log(values);
      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("days", values.durationDays || 0);
      formData.append("hours", values.durationHours || 0);
      formData.append("minutes", values.durationMinute || 0);
      formData.append("milestone_id", values.milestone);
      formData.append("company_id", values.company);
      formData.append("location_id", values.location);
      formData.append("description", values.description || "");
      formData.append("sort_order", values.sort_order);
      formData.append("min_employee", parseInt(values.min) || "");
      formData.append("max_employee", parseInt(values.max) || "");
      formData.append("version", values.version || "");
      formData.append("root_image_status", values.root_image || 0);
      formData.append("side_image_status", values.side_image || 0);
      formData.append("top_image_status", values.top_image || 0);
      // Check if new files are provided
      if (values.file && values.file.length > 0) {
        for (let i = 0; i < values.file.length; i++) {
          formData.append(`attached`, values.file[i]);
        }
      } else {
        formData.append("attached", []);
      }
      //Runs only for edit purpose
      if (values.attached) {
        if (activity_id !== "" && !isAdd) {
          for (let i = 0; i < values.attached.length; i++) {
            formData.append(`existFile[${i}]`, values.attached[i]);
          }
          // formData.append("existFile", values.attached);
        }
      }
      if (!apiError) {
        try {
          if (isAdd) {
            const response = await httpService.post(
              "activity/add_activity",
              formData
            );
            if (response) {
              toast.success("Activity Added Successfully", {
                position: "top-right",
              });
              formik.resetForm();
              handleClose();
              fetchActivity();
            }
          } else {
            const res = await httpService.put(
              `${"activity/edit_activity"}/${activity_id}`,
              formData
            );
            if (res) {
              toast.success("Activity Updated Successfully", {
                position: "top-right",
              });
              // formik.resetForm();
              handleClose();
              fetchActivity();
            }
          }
        } catch (error) {
          console.log(error);
        }
      }
    },
  });

  useEffect(() => {
    if (
      formik.values?.sort_order === "" &&
      companyId > 0 &&
      locationId > 0 &&
      activity_id === ""
    ) {
      autoSequenceOrder(
        { company_id: companyId, location_id: locationId },
        formik.setFieldValue
      );
    }
  }, [companyId, locationId, formik.values.sort_order]);

  const imageType = [
    {
      status: `root_image`,
      label: "Image 1",
    },
    {
      status: `top_image`,
      label: "Image 2",
    },
    {
      status: `side_image`,
      label: "Image 3",
    },
  ];

  return (
    <Modal
      className="task-modal"
      open={open}
      onClose={() => {
        handleCloseSave();
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <form onSubmit={formik.handleSubmit} encType="multipart/form-data">
        <div className="modal_block">
          <div className="modal_header">
            <div className="modal_title_wrapper">
              <h2 className="modal_title">
                {isAdd === true ? "Add Activity" : "Edit Activity"}
              </h2>
            </div>
            <div className="cancel-btn-wrap">
              <RxCross1
                onClick={() => {
                  handleCloseSave();
                }}
              />
            </div>
          </div>
          <div className="modal_content">
            <div className="row">
              <div className="item_increase">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>
                        Name<span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        id="name"
                        InputProps={{}}
                        placeholder="Enter Your Activity Name"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                      />
                      {formik.touched.name && formik.errors.name ? (
                        <div style={{ color: "red" }}>{formik.errors.name}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form_field_wrapper">
                      <label>
                        Duration<span style={{ color: "red" }}>*</span>
                      </label>
                      <TextField
                        type="number"
                        id="durationDays"
                        InputProps={{ inputProps: { min: 0 } }}
                        placeholder="DD"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.durationDays}
                      />
                      {formik.touched.durationDays &&
                      formik.errors.durationDays ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.durationDays}
                        </div>
                      ) : null}
                      {formik.touched.durationHours &&
                      formik.errors.durationHours ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.durationHours}
                        </div>
                      ) : null}
                      {formik.touched.durationMinute &&
                      formik.errors.durationMinute ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.durationMinute}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form_field_wrapper">
                      <label>&nbsp;</label>
                      <TextField
                        type="number"
                        id="durationHours"
                        InputProps={{ inputProps: { min: 0 } }}
                        placeholder="HH"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.durationHours}
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="form_field_wrapper">
                      <label>&nbsp;</label>
                      <TextField
                        type="number"
                        id="durationMinute"
                        InputProps={{ inputProps: { min: 0 } }}
                        placeholder="MM"
                        variant="outlined"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.durationMinute}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <SelectInput
                      label="Company"
                      name="company"
                      value={formik.values.company}
                      options={companies}
                      multiple={false}
                      onChange={(event) => {
                        formik.setFieldValue("company", event.target.value);
                        handleChangeLocationMilestone(
                          "company",
                          event.target.value
                        );
                      }}
                      onBlur={(e) => formik.handleBlur(e)}
                      error={formik.touched.company && formik.errors.company}
                      required={true}
                      displayEmpty
                      disabled={companyId > 0 ? true : false}
                    />
                  </div>

                  <div className="col-md-6">
                    <SelectInput
                      label="Location"
                      name="location"
                      value={formik.values.location}
                      options={locations}
                      multiple={false}
                      onChange={(event) => {
                        formik.setFieldValue("location", event.target.value);
                        handleChangeLocationMilestone(
                          "location",
                          event.target.value
                        );
                        const payload = {
                          company_id: formik.values?.company,
                          location_id: event.target.value,
                          sort_order: formik.values?.sort_order,
                          id: activity_id,
                        };
                        if (
                          formik.values?.company &&
                          formik.values?.sort_order &&
                          event.target.value
                        ) {
                          matchSequenceOrder(payload);
                        } else {
                          setApiError("");
                        }
                        if (activity_id === "") {
                          autoSequenceOrder(payload);
                        }
                      }}
                      onBlur={(e) => formik.handleBlur(e)}
                      error={formik.touched.location && formik.errors.location}
                      required={true}
                      displayEmpty
                      disabled={locationId > 0 ? true : false}
                    />
                  </div>

                  <div className="col-md-6">
                    <SelectInput
                      label="Milestone"
                      name="milestone"
                      value={formik.values.milestone}
                      options={milestoneNames}
                      multiple={false}
                      onChange={(event) => {
                        formik.setFieldValue("milestone", event.target.value);
                        handleChangeLocationMilestone(
                          "milestone",
                          event.target.value
                        );
                      }}
                      onBlur={(e) => formik.handleBlur(e)}
                      error={
                        formik.touched.milestone && formik.errors.milestone
                      }
                      required={true}
                      displayEmpty
                    />
                  </div>

                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>Sequence(#)</label>
                      <TextField
                        name="sort_order"
                        // className={classes.textField}
                        type="number"
                        InputProps={{ inputProps: { min: 0 } }}
                        placeholder="Enter Sequence Number"
                        value={formik.values.sort_order}
                        onChange={(event) => {
                          formik.setFieldValue(
                            "sort_order",
                            event.target.value
                          );
                          if (
                            formik.values?.company &&
                            formik.values?.location &&
                            event.target.value
                          ) {
                            const payload = {
                              company_id: formik.values?.company,
                              location_id: formik.values?.location,
                              sort_order: event.target.value,
                              id: activity_id,
                            };
                            matchSequenceOrder(payload);
                          } else {
                            setApiError("");
                          }
                        }}
                        onBlur={formik.handleBlur}
                      />
                      {apiError && (
                        <span style={{ color: "red" }}>{apiError}</span>
                      )}
                    </div>
                  </div>
                  <div className="col-md-6 employee_count">
                    <label>Employees</label>
                    <div className="row">
                      <div className="col-md-6 employee_item">
                        <div className="form_field_wrapper">
                          <label>Min</label>
                          <TextField
                            name="min"
                            type="number"
                            InputProps={{ inputProps: { min: 0 } }}
                            placeholder="Enter Min Emp"
                            value={formik.values.min}
                            onChange={(event) => {
                              formik.setFieldValue("min", event.target.value);
                            }}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                      </div>

                      <div className="col-md-6 employee_item">
                        <div className="form_field_wrapper">
                          <label>Max</label>
                          <TextField
                            name="max"
                            type="number"
                            InputProps={{ inputProps: { min: 0 } }}
                            placeholder="Enter Max Emp"
                            value={formik.values.max}
                            onChange={(event) => {
                              formik.setFieldValue("max", event.target.value);
                            }}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                      </div>
                      {formik.touched.min && formik.errors.min ? (
                        <div style={{ color: "red" }}>{formik.errors.min}</div>
                      ) : null}
                      {formik.touched.max && formik.errors.max ? (
                        <div style={{ color: "red" }}>{formik.errors.max}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>Reff Doc. Version(s)</label>
                      <TextField
                        name="Reff Doc. Version(s)"
                        type="text"
                        placeholder="Enter Version(s)"
                        value={formik.values.version}
                        onChange={(event) => {
                          formik.setFieldValue("version", event.target.value);
                        }}
                        onBlur={formik.handleBlur}
                      />
                    </div>
                  </div>
                  <div className="col-md-12">
                    <div className="form_field_wrapper">
                      <label>Description</label>
                      <div>
                        <TextareaAutosize
                          id="description"
                          className="w-100 text-area"
                          aria-label="Type Description"
                          minRows={3}
                          placeholder="Type Description"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.description}
                        />
                        {formik.touched.description &&
                        formik.errors.description ? (
                          <div style={{ color: "red" }}>
                            {formik.errors.description}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form_field_wrapper">
                      <label>
                        Image Category
                        {/* <span style={{ color: "red" }}>*</span> */}
                      </label>
                      <div className="days-arrenge d-flex flex-wrap flex-md-nowrap">
                        {imageType?.map((item, index) => {
                          return (
                            <div key={index} className="d-flex">
                              <div className="form_field_wrapper">
                                <FormGroup>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        name={item.status}
                                        checked={
                                          formik.values?.[item.status] === 1
                                        }
                                        onChange={(e) => {
                                          formik.setFieldValue(
                                            item.status,
                                            e.target.checked === true ? 1 : 0
                                          );
                                        }}
                                        onBlur={formik.handleBlur}
                                      />
                                    }
                                  />
                                </FormGroup>
                              </div>
                              <div className="form_field_wrapper">
                                <TextField
                                  defaultValue={item?.label}
                                  variant="outlined"
                                  InputProps={{ readOnly: true }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* {formik.touched.root_image && formik.errors.root_image ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.root_image}
                        </div>
                      ) : null}
                      {formik.touched.top_image && formik.errors.top_image ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.top_image}
                        </div>
                      ) : null}
                      {formik.touched.side_image && formik.errors.side_image ? (
                        <div style={{ color: "red" }}>
                          {formik.errors.side_image}
                        </div>
                      ) : null} */}
                    </div>
                  </div>

                  <FileInput
                    file={formik.values.file}
                    attachment={formik.values.attached}
                    handleBlur={formik.handleBlur}
                    name="file"
                    name2="attached"
                    setFieldValue={(fieldName, value) =>
                      formik.setFieldValue(fieldName, value)
                    }
                  />
                  {formik.errors.file && formik.touched.file && (
                    <span style={{ color: "red" }}>{formik.errors.file}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal_footer">
            <button
              onClick={() => {
                handleCloseSave();
              }}
              className="btn btn-border"
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {isAdd === true ? "Save" : "Update"}
            </button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AddEditActivity;
